import MultiselectEmployee from "@/entities/MultiselectEmployee";
import getFullName from "@/helpers/getFullName";
import DropdownOption from "../entities/DropdownOption";
import getUserFullData from "@/helpers/getUserFullData";

interface GetMultiselectEmployeesArgs {
  employees: MultiselectEmployee[];
  search: string;
  withEmailSearch?: boolean;
}

interface ExtendedDropdownOption extends DropdownOption {
  matches: number;
  userId?: string;
}

interface SearchConditions {
  givenName: string;
  familyName: string;
  email?: string
}

type FilterOutput = ExtendedDropdownOption[] | DropdownOption[];

const getMatchesCount = (
  searchConditions: SearchConditions,
  searchSeparatedParts: string[]
) => {
  const parts = new Set(searchSeparatedParts);
  const conditions = new Set();

  Object.entries(searchConditions).forEach(
    ([searchConditionKey, searchConditionValue]) => {
      parts.forEach(searchPart => {
        const match = searchConditionValue.toLowerCase().includes(searchPart);

        if (match) {
          conditions.add(searchConditionKey);
          conditions.add(searchPart);
        }
      });
    }
  );

  const averageNumberOfMatches = conditions.size / 2;

  if (parts.size === searchSeparatedParts.length) {
    return Math.floor(averageNumberOfMatches);
  }

  return Math.ceil(averageNumberOfMatches);
};

const getUserData = (employees: MultiselectEmployee[], withEmail?: boolean ): FilterOutput => {
  if (!withEmail) {
    return employees.map(({ id, user }) => ({
      ...user,
      name: getFullName(user.given_name!, user.family_name!),
      id,
      userId: user.id, // Add user ID for bookmark matching
    }))
  }

  return employees.map(({ id, user }) => ({
    ...user,
    name: getUserFullData(user.given_name!, user.family_name!, user.private_data?.email!),
    id,
    userId: user.id, // Add user ID for bookmark matching
  }))
}

export default ({
  employees,
  search,
  withEmailSearch,
}: GetMultiselectEmployeesArgs): FilterOutput => {
  if (employees.length === 0) {
    return [];
  }

  if (search.length === 0) {
    return getUserData(employees, withEmailSearch);
  }

  const searchString = search.toLowerCase();
  const searchSeparatedParts = searchString.split(" ").filter(Boolean);
  let matchedItems = 0;

  return employees.reduce<ExtendedDropdownOption[]>((listOptions, employee) => {
    const { user, id } = employee;
    const email = user.private_data?.email || "";
    const familyName = user.family_name || "";
    const givenName = user.given_name || "";

    let searchConditions: SearchConditions = { givenName, familyName };
    let userInfo = getFullName(givenName, familyName);

    if (withEmailSearch) {
      userInfo += ` - ${email}`;
      searchConditions = { ...searchConditions, email };
    }

    const matchesCount = getMatchesCount(
      searchConditions,
      searchSeparatedParts
    );

    if (matchesCount >= searchSeparatedParts.length) {
      if (matchesCount > matchedItems) {
        matchedItems = matchesCount;
        listOptions = [];
      }

      listOptions.push({
        ...employee.user,
        name: userInfo,
        id,
        userId: user.id, // Add user ID for bookmark matching
        matches: matchesCount,
      });
    }

    return listOptions;
  }, []);
};
